import { Injectable } from '@angular/core';
import { AuthService } from './auth.service';
import { FirebaseService } from './firebase.service';
import { getDownloadURL, getStorage, ref, uploadBytes } from 'firebase/storage';
import { getFunctions, httpsCallable } from 'firebase/functions';

export interface MembershipDocumentUploadPayload {
  file: File;
  fullName: string;
  userId: string;
  membershipId: string;
  mobileNumber?: string;
  provider?: string;
  membershipData?: any;
  email?: string;
}

export interface VerificationResult {
  status: 'verified' | 'soft_verified' | 'review' | 'failed';
  matchedSignals: string[];
  matchedCount: number;
  nameMatch: boolean;
  phoneMatch: boolean;
  flags: string[];
  reviewDocumentPath?: string;
}

@Injectable({ providedIn: 'root' })
export class MembershipsService {

  constructor(
    private authService: AuthService,
    private firebaseService: FirebaseService
  ) { }

  async uploadToTempStorage(file: File, filePath: string): Promise<string> {
    const storage = getStorage(this.firebaseService.app);
    const user = this.authService.getCurrentUser();
    if (!user) throw new Error('No authenticated user found.');
    try {
      const storageRef = ref(storage, `temp/${filePath}`);
      await uploadBytes(storageRef, file);
      return getDownloadURL(storageRef);
    } catch (error) {
      console.error('Upload error details:', {
        code: error.code,
        message: error.message,
        serverResponse: error.serverResponse,
        name: error.name,
        stack: error.stack
      });
      throw error;
    }
  }

  async submitMembershipDocument(payload: MembershipDocumentUploadPayload): Promise<VerificationResult> {
    const { file, fullName, userId, membershipId, mobileNumber, provider, membershipData, email } = payload;
    if (!membershipId) {
      throw new Error('Missing membership identifier.');
    }
    try {
      // Upload to 'temp' bucket
      const timestamp = Date.now();
      const safeName = file.name.replace(/[^a-zA-Z0-9.\-_]/g, '_');
      const filePath = `memberships/${userId}/${membershipId}/${timestamp}-${safeName}`;
      const fileUrl = await this.uploadToTempStorage(file, filePath);

      // Call the onCall function
      const functions = getFunctions();
      const verifyFunction = httpsCallable(functions, 'verifyMembershipDocument');
      const result = await verifyFunction({
        fileUrl,
        expectedName: fullName.trim(),
        userId,
        mobileNumber,
        provider,
        membershipId,
        membershipData,
        email,
        fileInfo: {
          name: file.name,
          size: file.size,
          contentType: file.type,
        },
      });
      return result.data as VerificationResult;
    } catch (err) {
      console.error('Upload or verification error:', err);
      throw err;
    }
  }
}
